// property-card.component.ts
import {Component, Input, Output, EventEmitter, OnInit} from '@angular/core';
import {CommonModule} from '@angular/common';
import {CurrencyPipe} from "@angular/common";
import {RouterModule} from '@angular/router';

@Component({
  selector: 'app-property-card',
  standalone: true,
  imports: [CommonModule, RouterModule],
  template: `
    <div *ngIf="listing" class="property active" style="cursor: pointer; position: relative;" (click)="onCardClick()" tabindex="0" role="button" (keydown.enter)="onCardClick()">
      <!-- Invisible but accessible full-card link for SSR/SEO and non-JS navigation -->
      <a class="stretched-link"
         [routerLink]="['/fulldetails', (listing.listingDetail?.address ?? listing.fullAddress)?.replaceAll(' ', '-'), listing.municipality?.replaceAll(' ', '-'), listing.MLSNUM]"
         [attr.aria-label]="'View full details for ' + (listing.listingDetail?.address ?? listing.fullAddress)"
         style="position:absolute; inset:0; z-index:1; text-indent:-9999px; overflow:hidden; pointer-events:none;"
         aria-hidden="true" tabindex="-1">
        View full details
      </a>

      <!-- Featured Image Section -->
      <div class="featured-image"
           [ngStyle]="getBackgroundImageStyle(listing?.listingMedia?.data?.[0]?.url)"
           style="position: relative; z-index: 0;">
        <div *ngIf="listing.banner"
             [class]="listing.banner.bannerClass"
             class="banner">
          <div [class]="listing.banner.lineClass"
               [innerHTML]="listing.banner.text">
          </div>
        </div>
      </div>

      <!-- Content Section -->
      <div class="content" style="position: relative; z-index: 0;">
        <!-- Price Row -->
        <div class="row">
          <div class="price">
            {{ formatPrice(listing.listingPrice) }}
            <span *ngIf="isRental">/ Week</span>
          </div>
        </div>

        <!-- Description Row -->
        <div class="row">
          <p class="description">
            <span class="sold" *ngIf="isSoldNotExclusive">{{ listing.listingStatus }}</span>
            <span class="exclusive" *ngIf="listing.exclusive">Exclusive</span>
            {{ listing.listingDetail?.address }}
          </p>
        </div>

        <!-- Property Features Row -->
        <div *ngIf="!isRental && listing.squareFootage && listing.squareFootage!=='0'" class="row">
          <p class="feet">{{ listing.squareFootage }} Sq. Ft.</p>
        </div>
        <div *ngIf="isRental && listing.listingDetail?.numBeds" class="row">
          <p class="feet">{{ listing.listingDetail?.numBeds }} Bedrooms</p>
        </div>
        <div *ngIf="isRental && (listing.listingDetail?.numBathsFull || listing.bathroomsTotal)" class="row">
          <p class="feet">{{ listing.listingDetail?.numBathsFull ?? listing.bathroomsTotal }} Bathrooms</p>
        </div>

        <!-- Links Row -->
        <div class="row">
          <ul class="links">
            <li *ngIf="listing.listingDetail?.virtualTour">
              <a href="{{listing.listingDetail?.virtualTour}}" target="_blank">Virtual Tour</a>
            </li>
            <li *ngIf="listing.listingDetail?.brochureLink">
              <a href="{{listing.listingDetail?.brochureLink}}" target="_blank">Brochure</a>
            </li>
          </ul>
        </div>
      </div>
    </div>
  `
})
export class PropertyCardComponent implements OnInit {
  @Input() listing: any;
  @Output() cardClick = new EventEmitter<any>();
  private supportsWebP = false;

  constructor(private currencyPipe: CurrencyPipe) {
  }

  async ngOnInit() {
    this.supportsWebP = await this.checkWebPSupport();
  }

  private checkWebPSupport(): Promise<boolean> {
    if (typeof Image === 'undefined') {
      return Promise.resolve(false);
    }
    return new Promise(resolve => {
      const img = new Image();
      img.onload = () => resolve(true);
      img.onerror = () => resolve(false);
      img.src = 'data:image/webp;base64,UklGRiIAAABXRUJQVlA4TBEAAAAvAAAAAAfQ//73v/+BiOh/AAA=';
    });
  }


  protected getBackgroundImageStyle(urlStr: string) {
    const imageUrl = this.fixThumbnailImage(urlStr);
    return {'background-image': `url("${imageUrl}")`};
  }

  protected fixThumbnailImage(urlStr: string) {
    if (!urlStr) {
      return '';
    }

    try {
      // Check if URL has already been converted to medium-optimized format
      if (urlStr.includes('muskoka_image_cache/resized/medium-optimized/')) {
        return urlStr;
      }

      // Check if URL is in the Muskoka image cache format
      if (urlStr.includes('dhauv7lm2gh1r.cloudfront.net/rets_image_cache/muskoka_image_cache/')) {
        return urlStr.replace(
          'https://dhauv7lm2gh1r.cloudfront.net/rets_image_cache/muskoka_image_cache/',
          'https://d2hg257h9htd0r.cloudfront.net/muskoka_image_cache/resized/medium-optimized/'
        );
      }

      // Handle standard cloudfront URLs
      const format = this.supportsWebP ? 'webp' : 'jpg';
      const url = new URL(urlStr);
      const filename = url.pathname.split('/').pop();

      return `https://d3hnn994gxsthv.cloudfront.net/fit-in/800x800/filters:format(${format}):quality(80)/${filename}`;

    } catch (error) {
      console.error('Error processing thumbnail URL:', error);
      return urlStr;
    }
  }


  get isRental(): boolean {
    return !!(this.listing?.listingStatus && this.listing.listingStatus.toLowerCase() === 'rental');
  }

  get isSoldNotExclusive(): boolean {
    return !!(this.listing?.listingStatus && this.listing.listingStatus.toLowerCase() === 'sold' && !this.listing.exclusive);
  }


  formatPrice(price: number): string {
    if (price == null) return '';
    return this.currencyPipe.transform(price, '', 'symbol', '1.0-0') || '';
  }

  onCardClick(): void {
    this.cardClick.emit(this.listing);
  }
}
